import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import HttpBackend from 'i18next-http-backend'
import LanguageDetector from 'i18next-browser-languagedetector'

/**
 * The six official languages of the United Nations.
 *
 * `label` is the ENDONYM — the word a language uses for itself. Somebody looking for their own
 * language scans a list for the word they recognise, not for our English name for it.
 *
 * This list must stay in step with backend/app/services/i18n.py, which decides what may be
 * SAVED. If the two ever drift, the backend refuses the unknown code and the person keeps the
 * language they had — the failure lands somewhere harmless rather than in a half-translated page.
 */
export const LANGUAGES = [
  { code: 'ar', label: 'العربية', english: 'Arabic', dir: 'rtl' },
  { code: 'de', label: 'Deutsch', english: 'German', dir: 'ltr' },
  { code: 'zh', label: '中文', english: 'Chinese', dir: 'ltr' },
  { code: 'en', label: 'English', english: 'English', dir: 'ltr' },
  { code: 'fr', label: 'Français', english: 'French', dir: 'ltr' },
  { code: 'it', label: 'Italiano', english: 'Italian', dir: 'ltr' },
  { code: 'ru', label: 'Русский', english: 'Russian', dir: 'ltr' },
  { code: 'es', label: 'Español', english: 'Spanish', dir: 'ltr' },
]

export const SUPPORTED = LANGUAGES.map((l) => l.code)

export const dirFor = (code) =>
  LANGUAGES.find((l) => l.code === (code || '').split('-')[0])?.dir || 'ltr'

/**
 * Tell the DOCUMENT what language it is in.
 *
 * `dir` is the one that matters and the one that is easy to forget: without it Arabic renders
 * left-to-right — every sentence technically correct and the whole page inside out. `lang`
 * matters less visibly but decides hyphenation, spellcheck, and what a screen reader sounds like.
 */
export function applyDocumentLanguage(code) {
  const lng = (code || 'en').split('-')[0]
  const dir = dirFor(lng)
  document.documentElement.setAttribute('lang', lng)
  document.documentElement.setAttribute('dir', dir)
  // Tailwind has no `rtl:` variant configured here, so a class is the honest hook for the
  // handful of places that need to mirror rather than merely flow.
  document.documentElement.classList.toggle('rtl', dir === 'rtl')
}

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED,
    // Only the BASE language is loaded: a browser set to fr-CA gets our `fr`, rather than
    // requesting a fr-CA folder that does not exist and falling back to English.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    // PRELOADED namespaces only — the two that appear on every screen. Every other namespace is
    // fetched the first time a component asks for it via useTranslation('x'). Listing them all
    // here would fire eighty-odd JSON requests on first paint, most for screens the person will
    // never open. (`finance` and `disputes` had files on disk but were absent from this list for
    // a long time, so every key in them rendered as its own name — hence the parity check.)
    ns: ['common', 'nav'],
    defaultNS: 'common',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      // The signed-in person's saved choice beats all of these — it is applied directly once
      // their profile arrives (see useAuth). These decide the language BEFORE anyone is known:
      // on the login page, and for a first visit.
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lng',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
  })

// Keep the document in step with i18next, including the very first resolution.
i18n.on('languageChanged', applyDocumentLanguage)
applyDocumentLanguage(i18n.resolvedLanguage || i18n.language)

export default i18n
